'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';

import AppLayout from '@/components/AppLayout';
import { useSheikhNotifications } from '@/lib/hooks/useSheikhNotifications';
import { SheikhNotificationToast, SheikhNotificationBell } from '@/components/SheikhNotifications';
import type { SheikhNotification } from '@/lib/hooks/useSheikhNotifications';
import { sendScholarAnsweredNotification } from '@/lib/email/transactional';

// ─── Types ────────────────────────────────────────────────────────────────────

type QuestionStatus = 'pending' | 'draft' | 'submitted_for_approval' | 'approved' | 'rejected';
type AnswerMode = 'text' | 'audio';

interface PendingQuestion {
  id: string;
  seniorName: string;
  seniorInitials: string;
  questionText?: string;
  questionAudioUrl?: string;
  questionType: 'text' | 'audio';
  specialisation: string;
  submittedAt: string;
  status: QuestionStatus;
  draftAnswer?: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_QUESTIONS: PendingQuestion[] = [
  {
    id: 'q-001',
    seniorName: 'Fatima Al-Rashid',
    seniorInitials: 'FA',
    questionText: 'Assalamu Alaikum Sheikh. I am 72 years old and have difficulty standing for long periods. Can I pray Fajr sitting down if my knees hurt too much to stand?',
    questionType: 'text',
    specialisation: 'Ibadah',
    submittedAt: '2026-07-01T08:30:00Z',
    status: 'pending',
  },
  {
    id: 'q-002',
    seniorName: 'Hassan Mwangi',
    seniorInitials: 'HM',
    questionText: 'Sheikh, I missed many prayers in my youth due to ignorance. How do I make up (qada) for years of missed prayers at my age?',
    questionType: 'text',
    specialisation: 'Fiqh',
    submittedAt: '2026-07-01T10:15:00Z',
    status: 'pending',
  },
  {
    id: 'q-003',
    seniorName: 'Aminah Osman',
    seniorInitials: 'AO',
    questionAudioUrl: '/mock-audio.webm',
    questionType: 'audio',
    specialisation: 'Quran',
    submittedAt: '2026-06-30T16:45:00Z',
    status: 'pending',
  },
  {
    id: 'q-004',
    seniorName: 'Ibrahim Suleiman',
    seniorInitials: 'IS',
    questionText: 'I have been diagnosed with diabetes and must break my fast for medication. How should I handle Ramadan fasting?',
    questionType: 'text',
    specialisation: 'Fiqh',
    submittedAt: '2026-06-30T09:00:00Z',
    status: 'submitted_for_approval',
    draftAnswer: 'Bismillah. For a diabetic who requires medication during fasting hours, Islam provides clear guidance...',
  },
  {
    id: 'q-005',
    seniorName: 'Khadija Yusuf',
    seniorInitials: 'KY',
    questionText: 'What is the correct way to perform tayammum (dry ablution) when I cannot use water due to a skin condition?',
    questionType: 'text',
    specialisation: 'Purification',
    submittedAt: '2026-06-29T14:20:00Z',
    status: 'approved',
    draftAnswer: 'Bismillah. Tayammum is a valid substitute for wudu when water is unavailable or harmful...',
  },
];

const SPECIALISATIONS = ['All', 'Fiqh', 'Ibadah', 'Quran', 'Tafsir', 'Purification', 'Aqeedah', 'Hadith', 'Dhikr'];

const STATUS_CONFIG: Record<QuestionStatus, { label: string; color: string; bg: string }> = {
  pending:                 { label: 'Pending',              color: 'text-amber-700',  bg: 'bg-amber-100' },
  draft:                   { label: 'Draft',                color: 'text-blue-700',   bg: 'bg-blue-100' },
  submitted_for_approval:  { label: 'Awaiting Approval',    color: 'text-violet-700', bg: 'bg-violet-100' },
  approved:                { label: 'Approved',             color: 'text-green-700',  bg: 'bg-green-100' },
  rejected:                { label: 'Rejected',             color: 'text-red-700',    bg: 'bg-red-100' },
};

// ─── Answer Panel ─────────────────────────────────────────────────────────────

interface AnswerPanelProps {
  question: PendingQuestion;
  onClose: () => void;
  onSubmitForApproval: (id: string, answer: string, mode: AnswerMode, audioBlob?: Blob) => void;
}

function AnswerPanel({ question, onClose, onSubmitForApproval }: AnswerPanelProps) {
  const [answerMode, setAnswerMode] = useState<AnswerMode>('text');
  const [answerText, setAnswerText] = useState(question.draftAnswer || '');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [submitStep, setSubmitStep] = useState<'compose' | 'confirm' | 'submitting' | 'done'>('compose');

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => chunksRef.current.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setRecordingSeconds(0);
      timerRef.current = setInterval(() => setRecordingSeconds((s) => s + 1), 1000);
    } catch {
      alert('Microphone access is required to record your answer.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const clearAudio = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingSeconds(0);
  };

  const formatTime = (s: number) =>
    `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  const canSubmit =
    answerMode === 'text' ? answerText.trim().length > 20 : !!audioBlob;

  const handleSubmit = async () => {
    setSubmitStep('submitting');
    await new Promise((r) => setTimeout(r, 1500));
    onSubmitForApproval(question.id, answerText, answerMode, audioBlob || undefined);
    setSubmitStep('done');
  };

  if (submitStep === 'done') {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-700 text-foreground mb-1">Submitted for Approval</h3>
        <p className="text-sm text-muted-foreground mb-6 max-w-xs">
          Your answer has been sent to the senior admin for review before delivery to the senior.
        </p>
        <button
          onClick={onClose}
          className="px-6 py-2.5 bg-violet-600 text-white rounded-xl font-600 text-sm hover:bg-violet-700 transition-colors"
        >
          Back to Questions
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Panel Header */}
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-700 text-foreground text-base">Compose Answer</h2>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Question Preview */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-full bg-amber-200 flex items-center justify-center text-amber-800 text-xs font-700">
            {question.seniorInitials}
          </div>
          <span className="text-sm font-600 text-amber-900">{question.seniorName}</span>
          <span className="ml-auto text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-500">
            {question.specialisation}
          </span>
        </div>
        {question.questionType === 'text' ? (
          <p className="text-sm text-amber-900 leading-relaxed">{question.questionText}</p>
        ) : (
          <div className="flex items-center gap-2 text-sm text-amber-800">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
            Audio question recorded
          </div>
        )}
      </div>

      {/* Approval Notice */}
      <div className="flex items-start gap-2.5 bg-violet-50 border border-violet-200 rounded-xl p-3.5 mb-5">
        <svg className="w-4 h-4 text-violet-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-xs text-violet-800 leading-relaxed">
          Your answer will be reviewed by a senior admin before being delivered privately to the senior. Only approved answers are sent.
        </p>
      </div>

      {/* Answer Mode Toggle */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        {(['text', 'audio'] as AnswerMode[]).map((mode) => (
          <button
            key={mode}
            onClick={() => setAnswerMode(mode)}
            className={`flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-600 transition-all ${
              answerMode === mode
                ? 'bg-violet-600 text-white border-violet-600'
                : 'border-border bg-background text-muted-foreground hover:bg-muted'
            }`}
          >
            {mode === 'text' ? (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Type Answer
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
                Record Answer
              </>
            )}
          </button>
        ))}
      </div>

      {/* Text Answer */}
      {answerMode === 'text' && (
        <div className="flex-1 flex flex-col mb-4">
          <label className="block text-sm font-600 text-foreground mb-2">Your Answer</label>
          <textarea
            value={answerText}
            onChange={(e) => setAnswerText(e.target.value)}
            placeholder="Begin with Bismillah… Provide a clear, compassionate answer grounded in authentic Islamic scholarship."
            rows={7}
            className="flex-1 w-full px-4 py-3 border border-border rounded-xl bg-background text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-500 transition-colors resize-none"
          />
          <p className="text-xs text-muted-foreground mt-1.5 text-right">{answerText.length} characters</p>
        </div>
      )}

      {/* Audio Answer */}
      {answerMode === 'audio' && (
        <div className="flex-1 mb-4">
          {!audioBlob ? (
            <div className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-border rounded-xl">
              {isRecording ? (
                <>
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-3 animate-pulse">
                    <div className="w-5 h-5 bg-red-500 rounded-full" />
                  </div>
                  <p className="text-sm font-600 text-red-600 mb-1">Recording…</p>
                  <p className="text-2xl font-700 text-foreground mb-4 font-mono">{formatTime(recordingSeconds)}</p>
                  <button
                    onClick={stopRecording}
                    className="px-5 py-2.5 bg-red-600 text-white rounded-xl font-600 text-sm hover:bg-red-700 transition-colors"
                  >
                    Stop Recording
                  </button>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mb-3">
                    <svg className="w-8 h-8 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4 text-center px-4">
                    Record your answer as an audio message
                  </p>
                  <button
                    onClick={startRecording}
                    className="px-5 py-2.5 bg-violet-600 text-white rounded-xl font-600 text-sm hover:bg-violet-700 transition-colors"
                  >
                    Start Recording
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="border border-border rounded-xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-600 text-foreground">Audio Answer Recorded</p>
                  <p className="text-xs text-muted-foreground">{formatTime(recordingSeconds)} duration</p>
                </div>
                <button onClick={clearAudio} className="text-xs text-red-500 hover:text-red-700 font-500 transition-colors">
                  Re-record
                </button>
              </div>
              {audioUrl && (
                <audio controls src={audioUrl} className="w-full h-9 rounded-lg" />
              )}
            </div>
          )}
        </div>
      )}

      {/* Submit Button */}
      {submitStep === 'confirm' ? (
        <div className="space-y-2">
          <p className="text-sm text-center text-muted-foreground mb-2">
            Submit this answer for admin approval?
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setSubmitStep('compose')}
              className="py-2.5 border border-border rounded-xl text-sm font-600 text-muted-foreground hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="py-2.5 bg-violet-600 text-white rounded-xl text-sm font-600 hover:bg-violet-700 transition-colors"
            >
              Confirm & Submit
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setSubmitStep('confirm')}
          disabled={!canSubmit || submitStep === 'submitting'}
          className="w-full py-3 bg-gradient-to-r from-violet-600 to-purple-700 text-white font-600 rounded-xl text-sm hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {submitStep === 'submitting' ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Submitting…
            </span>
          ) : (
            'Submit for Approval →'
          )}
        </button>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SheikhDashboardPage() {
  const [questions, setQuestions] = useState<PendingQuestion[]>(MOCK_QUESTIONS);
  const [filterSpec, setFilterSpec] = useState('All');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'submitted_for_approval' | 'approved'>('all');
  const [selectedQuestion, setSelectedQuestion] = useState<PendingQuestion | null>(null);
  const [search, setSearch] = useState('');

  // Notification state
  const [bellOpen, setBellOpen] = useState(false);
  const [toastQueue, setToastQueue] = useState<SheikhNotification[]>([]);

  // In a real app, scholarId would come from auth context + scholar profile lookup
  // Using undefined here so the hook gracefully no-ops without a real scholar ID
  const { notifications, unreadCount, markAllRead, markRead, dismissNotification } =
    useSheikhNotifications(undefined);

  // When a new notification arrives, add it to the toast queue
  const prevCountRef = useRef(0);
  useEffect(() => {
    if (notifications.length > 0 && notifications[0] && !notifications[0].read) {
      const newest = notifications[0];
      const alreadyQueued = toastQueue.some((t) => t.id === newest.id);
      if (!alreadyQueued && prevCountRef.current < notifications.length) {
        setToastQueue((prev) => [newest, ...prev].slice(0, 3));
      }
    }
    prevCountRef.current = notifications.length;
  }, [notifications]);

  const handleDismissToast = useCallback((id: string) => {
    setToastQueue((prev) => prev.filter((t) => t.id !== id));
    dismissNotification(id);
  }, [dismissNotification]);

  const handleViewQuestion = useCallback((questionId: string) => {
    const q = questions.find((q) => q.id === questionId);
    if (q) setSelectedQuestion(q);
  }, [questions]);

  const filtered = questions.filter((q) => {
    const matchSpec = filterSpec === 'All' || q.specialisation === filterSpec;
    const matchStatus =
      filterStatus === 'all' ||
      (filterStatus === 'pending' && q.status === 'pending') ||
      (filterStatus === 'submitted_for_approval' && q.status === 'submitted_for_approval') ||
      (filterStatus === 'approved' && q.status === 'approved');
    const matchSearch =
      !search ||
      q.seniorName.toLowerCase().includes(search.toLowerCase()) ||
      (q.questionText || '').toLowerCase().includes(search.toLowerCase()) ||
      q.specialisation.toLowerCase().includes(search.toLowerCase());
    return matchSpec && matchStatus && matchSearch;
  });

  const pendingCount = questions.filter((q) => q.status === 'pending').length;
  const awaitingCount = questions.filter((q) => q.status === 'submitted_for_approval').length;

  const handleSubmitForApproval = async (id: string, answer: string, mode: AnswerMode, audioBlob?: Blob) => {
    const question = questions.find((q) => q.id === id);
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === id
          ? { ...q, status: 'submitted_for_approval' as QuestionStatus, draftAnswer: answer }
          : q
      )
    );
    setSelectedQuestion(null);

    // Email: notify senior that their scholar has composed an answer (pending admin review)
    if (question) {
      try {
        // In production, fetch the senior's email from the DB using question.id
        // For now we fire the email helper — it will silently fail if email is unavailable
        const seniorEmail = (question as any).seniorEmail as string | undefined;
        if (seniorEmail) {
          await sendScholarAnsweredNotification({
            email: seniorEmail,
            scholarName: 'Your Sheikh',
            questionPreview: question.questionText || 'Your audio question',
          });
        }
      } catch (err) {
        console.error('[Email] Failed to send scholar_answered notification:', err);
      }
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <AppLayout activeRoute="/sheikh/dashboard">
      {/* Toast Notifications — fixed top-right */}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-3 pointer-events-none">
        {toastQueue.map((notif) => (
          <div key={notif.id} className="pointer-events-auto">
            <SheikhNotificationToast
              notification={notif}
              onDismiss={handleDismissToast}
              onView={handleViewQuestion}
            />
          </div>
        ))}
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 bg-gradient-to-br from-violet-600 to-purple-700 rounded-xl flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <span className="text-xs font-600 text-violet-600 bg-violet-100 px-2 py-0.5 rounded-full">Verified Sheikh</span>
              </div>
              <h1 className="text-2xl font-800 text-foreground mb-1">Sheikh Dashboard</h1>
              <p className="text-muted-foreground text-sm">
                Review and answer private questions from seniors. All answers go through admin approval before delivery.
              </p>
            </div>

            {/* Stats + Notification Bell */}
            <div className="flex items-start gap-3 flex-wrap">
              {/* Notification Bell */}
              <div className="flex items-center">
                <SheikhNotificationBell
                  notifications={notifications}
                  unreadCount={unreadCount}
                  isOpen={bellOpen}
                  onToggle={() => setBellOpen((o) => !o)}
                  onMarkAllRead={markAllRead}
                  onDismiss={dismissNotification}
                  onViewQuestion={(qId) => {
                    handleViewQuestion(qId);
                    setBellOpen(false);
                  }}
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-center min-w-[80px]">
                <p className="text-2xl font-800 text-amber-700">{pendingCount}</p>
                <p className="text-xs text-amber-600 font-500">Pending</p>
              </div>
              <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-3 text-center min-w-[80px]">
                <p className="text-2xl font-800 text-violet-700">{awaitingCount}</p>
                <p className="text-xs text-violet-600 font-500">In Review</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-center min-w-[80px]">
                <p className="text-2xl font-800 text-green-700">
                  {questions.filter((q) => q.status === 'approved').length}
                </p>
                <p className="text-xs text-green-600 font-500">Approved</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">

          {/* Left: Question List */}
          <div className={`flex-1 min-w-0 ${selectedQuestion ? 'lg:max-w-[55%]' : ''}`}>

            {/* Filters */}
            <div className="bg-card border border-border rounded-2xl p-4 mb-4">
              {/* Search */}
              <div className="relative mb-3">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search questions or senior name…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 border border-border rounded-xl bg-background text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-500 transition-colors"
                />
              </div>

              {/* Status Filter */}
              <div className="flex gap-2 flex-wrap mb-3">
                {(['all', 'pending', 'submitted_for_approval', 'approved'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setFilterStatus(s)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-600 transition-all ${
                      filterStatus === s
                        ? 'bg-violet-600 text-white'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {s === 'all' ? 'All' : s === 'submitted_for_approval' ? 'In Review' : s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>

              {/* Specialisation Filter */}
              <div className="flex gap-1.5 flex-wrap">
                {SPECIALISATIONS.map((sp) => (
                  <button
                    key={sp}
                    onClick={() => setFilterSpec(sp)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-500 transition-all ${
                      filterSpec === sp
                        ? 'bg-purple-600 text-white' :'border border-border text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {sp}
                  </button>
                ))}
              </div>
            </div>

            {/* Question Cards */}
            <div className="space-y-3">
              {filtered.length === 0 ? (
                <div className="bg-card border border-border rounded-2xl p-10 text-center">
                  <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z" />
                    </svg>
                  </div>
                  <p className="text-sm font-600 text-foreground mb-1">No questions found</p>
                  <p className="text-xs text-muted-foreground">Try adjusting your filters</p>
                </div>
              ) : (
                filtered.map((q) => {
                  const statusCfg = STATUS_CONFIG[q.status];
                  const isSelected = selectedQuestion?.id === q.id;
                  return (
                    <div
                      key={q.id}
                      onClick={() => setSelectedQuestion(isSelected ? null : q)}
                      className={`bg-card border rounded-2xl p-4 cursor-pointer transition-all hover:shadow-md ${
                        isSelected
                          ? 'border-violet-400 ring-2 ring-violet-200'
                          : 'border-border hover:border-violet-200'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Avatar */}
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-xs font-700 flex-shrink-0">
                          {q.seniorInitials}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-600 text-foreground text-sm">{q.seniorName}</span>
                            <span className={`text-xs font-600 px-2 py-0.5 rounded-full ${statusCfg.bg} ${statusCfg.color}`}>
                              {statusCfg.label}
                            </span>
                            <span className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full font-500 ml-auto">
                              {q.specialisation}
                            </span>
                          </div>

                          {q.questionType === 'text' ? (
                            <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
                              {q.questionText}
                            </p>
                          ) : (
                            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                              </svg>
                              Audio question
                            </div>
                          )}

                          <div className="flex items-center gap-3 mt-2">
                            <span className="text-xs text-muted-foreground">{formatDate(q.submittedAt)}</span>
                            {q.status === 'pending' && (
                              <span className="text-xs font-600 text-violet-600 hover:underline">
                                Tap to answer →
                              </span>
                            )}
                            {q.status === 'submitted_for_approval' && (
                              <span className="text-xs text-violet-600 font-500 flex items-center gap-1">
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Awaiting admin review
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Right: Answer Panel */}
          {selectedQuestion && (
            <div className="lg:w-[45%] flex-shrink-0">
              <div className="bg-card border border-border rounded-2xl p-5 sticky top-6">
                {selectedQuestion.status === 'pending' || selectedQuestion.status === 'draft' ? (
                  <AnswerPanel
                    question={selectedQuestion}
                    onClose={() => setSelectedQuestion(null)}
                    onSubmitForApproval={handleSubmitForApproval}
                  />
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-700 text-foreground text-base">Answer Details</h2>
                      <button
                        onClick={() => setSelectedQuestion(null)}
                        className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className={`inline-flex items-center gap-1.5 text-xs font-600 px-3 py-1.5 rounded-full mb-4 ${STATUS_CONFIG[selectedQuestion.status].bg} ${STATUS_CONFIG[selectedQuestion.status].color}`}>
                      {STATUS_CONFIG[selectedQuestion.status].label}
                    </div>
                    {selectedQuestion.draftAnswer && (
                      <div className="bg-muted rounded-xl p-4">
                        <p className="text-xs font-600 text-muted-foreground mb-2">Your Answer</p>
                        <p className="text-sm text-foreground leading-relaxed">{selectedQuestion.draftAnswer}</p>
                      </div>
                    )}
                    {selectedQuestion.status === 'submitted_for_approval' && (
                      <p className="text-xs text-muted-foreground mt-3 text-center">
                        This answer is pending admin review before delivery to the senior.
                      </p>
                    )}
                    {selectedQuestion.status === 'approved' && (
                      <p className="text-xs text-green-600 mt-3 text-center font-500">
                        ✓ This answer has been approved and delivered to the senior.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
